import { EditorType } from '@wix/platform-editor-sdk';
import { EnhancedError } from '../utils/EnhancedError';
import { getWidgetId } from '../utils/widgetUtils';
import { MetaSiteManager } from '../services/MetaSiteManager';
import { getAppDescriptorByAppData } from '../editor';
import { PANEL_CODE_OVERRIDE } from '../constants';
import { getAppLanguages, } from '../editor/remoteStructureFetcher/remoteStructureFetcher';
import { getOverrideByPanelPage } from '../utils/urlBuilder';
import { isRTL } from '../utils/languageUtils';
export const PANEL_EXTENSION_DUMMY_URL = 'http://PANEL_URL.dummy';
const token = 'token';
const NEW_MIN_PANEL_HEIGHT = 44;
const MAX_PANEL_HEIGHT = 525;
const RESPONSIVE_WIDTH = 260;
const CLASSIC_WIDTH = 288;
const WIDGET_COMP_TYPE = 'platform.components.AppWidget';
const BUILDER_TYPE_PREFIX = 'platform.builder';
const APP_DEF_ID_SUFFIX = 'blocks-widget-service';
function isRoutePathOption(options) {
    return !!options?.routePath;
}
function isDevCenterComponentIdOption(options) {
    return !!options?.devCenterComponentId;
}
function isPageIdOption(options) {
    return !!options?.pageId;
}
export class PanelsApi {
    constructor(editorSDK, editorType, fedopsLogger, experiments, httpClient, appDescriptorApi) {
        this.editorSDK = editorSDK;
        this.editorType = editorType;
        this.fedopsLogger = fedopsLogger;
        this.experiments = experiments;
        this.httpClient = httpClient;
        this.appDescriptorApi = appDescriptorApi;
        this.metaSiteManager = new MetaSiteManager();
        this.panelsUrls = {};
        this.openBlocksPanel = async (panelIdentifier, componentRef, options = {}) => {
            const customParams = { componentRef, panelIdentifier };
            if (!this.baseUrl) {
                throw new Error('baseUrl was not found');
            }
            if (!this.appDefinitionId) {
                throw new Error('appDefinitionId was not found');
            }
            if (!this.experiments.enabled('specs.blocks-widget-services.deprecatePanelApplicationId')) {
                if (!this.applicationId) {
                    throw new Error('applicationId was not found');
                }
            }
            const revision = options.overrides?.revision ?? this.devSiteSiteRevision;
            if (!revision) {
                throw new Error('siteRevision was not found in site assets');
            }
            const { url, width, height, title, helpId } = await this.getPanelData({
                panelIdentifier,
                applicationId: this.applicationId,
                appDefinitionId: this.appDefinitionId,
                revision,
                componentRef,
                version: this.version,
            });
            const onOpen = async (panelToken) => {
                await this.editorSDK.editor.showPanelPreloader(panelToken);
            };
            await this.editorSDK.editor.setAppAPI('token', {
                onPanelLoaded: /* istanbul ignore next */ () => {
                    this.fedopsLogger.interactionEnded('panel-loaded');
                    this.fedopsLogger.interactionEnded('panel-execute-user-code');
                },
                /* istanbul ignore next */
                onPanelViewerInstanceInitialized: () => {
                    this.fedopsLogger.interactionEnded('panel-viewer-instance-initialize');
                    this.fedopsLogger.interactionStarted('panel-execute-user-code');
                },
            }, {
                appDefinitionId: `${this.appDefinitionId}-${APP_DEF_ID_SUFFIX}`,
            });
            // see the description of the fedops interactions in README.md file
            this.fedopsLogger.interactionStarted('panel-loaded', {
                customParams,
            });
            this.fedopsLogger.interactionStarted('panel-viewer-instance-initialize', {
                customParams,
            });
            const componentPanelOption = {
                url,
                componentRef,
                width,
                height,
                title: options.overrides?.title || title,
                helpId,
            };
            await this.editorSDK.editor.openComponentPanelWithSiteContext('token', componentPanelOption, onOpen);
        };
        this.openDashboardPanel = async (options) => {
            const url = await this.buildDashboardUrl(options);
            await this.editorSDK.editor.openDashboardPanel('token', {
                url,
                closeOtherPanels: false,
            });
        };
    }
    getPanelBaseUrl(panelData, panelPageId) {
        if (this.experiments.enabled('specs.blocks-widget-services.usePanelExtension') &&
            this.panelsUrls[panelPageId] &&
            this.panelsUrls[panelPageId] !== PANEL_EXTENSION_DUMMY_URL) {
            return this.panelsUrls[panelPageId];
        }
        return `${this.baseUrl}/${panelData.pageUrl}`;
    }
    async getPanelData({ panelIdentifier, applicationId, componentRef, appDefinitionId, revision, version, }) {
        /* istanbul ignore if  */
        if (!this.appDescriptor) {
            throw new Error('appDescriptor was not found');
        }
        const parentWidgets = await this.getParentWidgets(componentRef);
        if (parentWidgets.length === 0) {
            throw new Error('No parent widget ref was found');
        }
        const firstParentWidget = parentWidgets[0];
        const panelData = await this.getPanelDescriptor(parentWidgets, panelIdentifier);
        if (!panelData) {
            throw new EnhancedError('Panel was not found in appDescriptor', {
                panelIdentifier,
                appDescriptor: this.appDescriptor,
            });
        }
        const locale = await this.getPanelLanguage(this.appDescriptor);
        const editorUrlOverrides = await this.editorSDK.info.getArtifactOverrides('TOKEN');
        const panelPageId = this.getPanelPageId(panelData);
        const editorLanguage = await this.editorSDK.environment.getLocale();
        const siteLanguage = await this.getSiteLanguage();
        const isEditorLanguageRTL = isRTL(editorLanguage);
        const isSiteLanguageRTL = isRTL(siteLanguage);
        const appRevisionOverride = await this.getAppRevisionOverride(appDefinitionId);
        const siteRevision = appRevisionOverride || revision;
        const { siteOwnerId } = await this.editorSDK.info.getAppInstancePayload('TOKEN');
        const userId = await this.editorSDK.info.getUserId('TOKEN');
        const metaSiteId = await this.editorSDK.info.getMetaSiteId('TOKEN');
        if (!this.panelsUrls[panelPageId] && !panelData.pageUrl) {
            throw new Error(`Neither panel extension nor page URL for panel ${panelPageId} was found`);
        }
        const baseUrl = this.getPanelBaseUrl(panelData, panelPageId);
        const url = this.buildPanelUrl({
            baseUrl,
            applicationId,
            appDefinitionId,
            componentRef: firstParentWidget,
            siteRevision,
            locale,
            editorUrlOverrides,
            panelPageId,
            editorLanguage,
            siteLanguage,
            isEditorLanguageRTL,
            isSiteLanguageRTL,
            userId,
            metaSiteId,
            siteOwnerId,
            version,
        });
        const ceiledHeight = Math.ceil(panelData.height);
        return {
            url,
            title: panelData.title,
            width: this.editorType === EditorType.Responsive
                ? RESPONSIVE_WIDTH
                : CLASSIC_WIDTH,
            height: Math.max(NEW_MIN_PANEL_HEIGHT, Math.min(MAX_PANEL_HEIGHT, ceiledHeight)),
            helpId: panelData.helpId,
        };
    }
    async getSiteLanguage() {
        const isMultilingualEnabled = await this.editorSDK.language.multilingual.isEnabled('TOKEN');
        const siteLanguage = isMultilingualEnabled
            ? await this.editorSDK.language.current.get('TOKEN')
            : await this.editorSDK.document.info.getRegionalLanguage('TOKEN');
        return siteLanguage;
    }
    async getAppRevisionOverride(appDefinitionId) {
        const appRevisionOverrides = await this.editorSDK.document.info.getAppRevisionOverrides('TOKEN');
        if (!appRevisionOverrides) {
            return undefined;
        }
        const override = appRevisionOverrides[appDefinitionId];
        return override ? override.toString() : undefined;
    }
    async getPanelLanguage(appDescriptor) {
        const editorLanguage = await this.editorSDK.environment.getLocale();
        if (!appDescriptor.languages) {
            return editorLanguage;
        }
        const { enabledLanguages, mainLanguage } = appDescriptor.languages;
        return enabledLanguages.includes(editorLanguage)
            ? editorLanguage
            : mainLanguage;
    }
    buildPanelUrl({ baseUrl, applicationId, appDefinitionId, componentRef, siteRevision, locale, editorUrlOverrides, panelPageId, editorLanguage, siteLanguage, isEditorLanguageRTL, isSiteLanguageRTL, userId, metaSiteId, siteOwnerId, version, }) {
        const url = new URL(baseUrl);
        url.searchParams.set('sdkVersion', this.editorSDK.info.getSdkVersion(token).version);
        if (!this.experiments.enabled('specs.blocks-widget-services.deprecatePanelApplicationId')) {
            url.searchParams.set('applicationId', `${applicationId}`);
        }
        url.searchParams.set('appDefinitionId', appDefinitionId);
        url.searchParams.set('componentRef', JSON.stringify(componentRef));
        url.searchParams.set('lang', locale);
        url.searchParams.set('editorType', this.editorType);
        // TODO: get rid of these languare params under https://jira.wixpress.com/browse/WBL-6185
        url.searchParams.set('editorLanguage', editorLanguage);
        url.searchParams.set('siteLanguage', siteLanguage);
        url.searchParams.set('isEditorLanguageRTL', isEditorLanguageRTL.toString());
        url.searchParams.set('isSiteLanguageRTL', isSiteLanguageRTL.toString());
        if (version) {
            url.searchParams.set('version', version);
        }
        url.searchParams.set('siteRevision', siteRevision);
        url.searchParams.set('userId', userId);
        url.searchParams.set('siteOwnerId', siteOwnerId);
        url.searchParams.set('disableHtmlEmbeds', 'true');
        url.searchParams.set('msId', metaSiteId);
        Object.entries(editorUrlOverrides).forEach(([overrideKey, overrideValue]) => {
            let resultOverride = overrideValue;
            if (overrideKey === PANEL_CODE_OVERRIDE) {
                resultOverride = getOverrideByPanelPage(overrideValue, panelPageId);
            }
            if (resultOverride) {
                url.searchParams.set(overrideKey, resultOverride);
            }
        });
        return url.toString();
    }
    getPanelPageId(panelData) {
        const { rootCompId } = panelData;
        return rootCompId[0] === '#' ? rootCompId.substring(1) : rootCompId;
    }
    async getPanelDescriptor(parentWidgets, panelIdentifier) {
        /* istanbul ignore if  */
        if (!this.appDescriptor) {
            throw new Error('appDescriptor was not found');
        }
        const panelDescriptor = this.appDescriptor.panels[panelIdentifier];
        // panel identifier used to be the panel name. in this case we won't find
        // it in the panels object, so we will try to find it the old way
        // TODO - migrate all panels to use the panel identifier and remove this code WBL-8121
        if (!panelDescriptor) {
            // todo bi/logger
            return this.getPanelDescriptorByName(parentWidgets, panelIdentifier);
        }
        return panelDescriptor;
    }
    async getPanelDescriptorByName(parentWidgets, panelIdentifier) {
        /* istanbul ignore if  */
        if (!this.appDescriptor) {
            throw new Error('appDescriptor was not found');
        }
        for (const widget of parentWidgets) {
            const widgetId = await getWidgetId(this.editorSDK, widget);
            if (!widgetId) {
                continue;
            }
            const widgetDescriptor = this.appDescriptor.widgets[widgetId];
            if (!widgetDescriptor) {
                continue;
            }
            const panelDescriptor = widgetDescriptor.panels[panelIdentifier] ||
                Object.values(widgetDescriptor.panels).find((panel) => panel.name === panelIdentifier);
            if (panelDescriptor) {
                return panelDescriptor;
            }
        }
        return undefined;
    }
    async getParentWidgets(componentRef) {
        const ancestors = await this.editorSDK.components.getAncestors('token', {
            componentRef,
        });
        const components = [componentRef, ...ancestors];
        const listOfComponentsTypes = await Promise.all(components.map(async (component) => {
            const compType = await this.editorSDK.components.getType('token', {
                componentRef: component,
            });
            return {
                compType,
                componentRef: component,
            };
        }));
        const items = listOfComponentsTypes
            .filter(({ compType }) => compType === WIDGET_COMP_TYPE ||
            compType.startsWith(BUILDER_TYPE_PREFIX))
            .map((x) => x.componentRef);
        return items;
    }
    async initialize() {
        const isGetSiteAssetVersionDynamically = this.experiments.enabled('specs.blocks-widget-services.getSiteAssetVersionDynamically');
        this.appDefinitionId = await this.editorSDK.info.getAppDefinitionId(token);
        const appRevisionOverride = await this.getAppRevisionOverride(this.appDefinitionId);
        const appData = (await this.editorSDK.tpa.app.getDataByAppDefId('token', this.appDefinitionId));
        this.appData = appData;
        const appDescriptor = await this.getAppDescriptor(appData, isGetSiteAssetVersionDynamically, appRevisionOverride);
        const panels = Object.values(appDescriptor.widgets).reduce((acc, widget) => ({ ...acc, ...widget.panels }), {});
        this.appDescriptor = {
            ...appDescriptor,
            panels,
        };
        if (!this.experiments.enabled('specs.blocks-widget-services.deprecatePanelApplicationId')) {
            this.applicationId = appData.applicationId;
        }
        const siteAssets = appData.appFields.platform?.baseUrls?.siteAssets;
        if (!siteAssets) {
            throw new Error('siteAssets were not found in appData');
        }
        const searchParams = new URLSearchParams(siteAssets);
        const devSiteMetaSiteId = searchParams.get('metaSiteId') || undefined;
        this.devSiteSiteRevision = searchParams.get('siteRevision') || undefined;
        this.baseUrl = await this.getBaseUrl(appData, devSiteMetaSiteId);
        this.panelsUrls = this.getPanelsUrls(appData);
        this.version = this.getArtifactVersion(appData);
    }
    async getAppDescriptor(appData, isGetSiteAssetVersionDynamically, appRevisionOverride) {
        if (this.appDescriptorApi) {
            return this.appDescriptorApi.getAppDescriptor({
                appData,
                appRevisionOverride,
            });
        }
        const [appDescriptor, languages] = await Promise.all([
            getAppDescriptorByAppData(appData, {
                httpClient: this.httpClient,
                editorSDK: isGetSiteAssetVersionDynamically
                    ? this.editorSDK
                    : undefined,
                appRevisionOverride,
            }),
            getAppLanguages(appData),
        ]);
        return { ...appDescriptor, languages };
    }
    getArtifactVersion(appData) {
        const platformComponent = appData.components.find((x) => x.type === 'PLATFORM');
        if (!platformComponent) {
            return;
        }
        const editorScriptUrl = platformComponent.data.editorScriptUrl;
        if (!editorScriptUrl) {
            return;
        }
        return this.extractVersionFromArtifactUrl(editorScriptUrl);
    }
    extractVersionFromArtifactUrl(url) {
        const baseUrl = 'https://static.parastorage.com/services/';
        if (!url.startsWith(baseUrl)) {
            return;
        }
        const urlParts = url.replace(baseUrl, '').split('/');
        const [, version] = urlParts;
        return version;
    }
    async getBaseUrl(appData, metaSiteId) {
        const studioComponent = appData.components.find((x) => x.type === 'STUDIO');
        if (!studioComponent) {
            throw new Error('Studio component has not been found!');
        }
        const blocksDevSiteUrl = studioComponent.data.baseUrls?.blocks_devSiteUrl;
        if (blocksDevSiteUrl) {
            return blocksDevSiteUrl;
        }
        if (!metaSiteId) {
            throw new Error('metaSiteId was not found in site assets');
        }
        return this.metaSiteManager.getSiteUrl(metaSiteId);
    }
    getPanelsUrls(appData) {
        return appData.components.reduce((acc, comp) => {
            if (comp.type === 'PANEL') {
                acc[comp.data.referenceId] = comp.data.url;
                return acc;
            }
            return acc;
        }, {});
    }
    async buildDashboardUrl(options) {
        const appUrlSlug = `app/${this.appDefinitionId}`;
        if (!options) {
            return appUrlSlug;
        }
        if (isDevCenterComponentIdOption(options)) {
            const routePath = this.getDashboardRoutePath(options.devCenterComponentId);
            if (routePath) {
                return `${appUrlSlug}/${routePath}`;
            }
        }
        if (isRoutePathOption(options)) {
            return `${appUrlSlug}/${options.routePath}`;
        }
        if (isPageIdOption(options)) {
            const routePath = this.getRoutePathByPageId(options.pageId);
            if (routePath) {
                return `${appUrlSlug}/${routePath}`;
            }
        }
        return appUrlSlug;
    }
    getRoutePathByPageId(pageId) {
        const dashboardDescriptor = this.appDescriptor?.dashboards?.find((x) => (x.rootCompId.startsWith('#')
            ? x.rootCompId.slice(1)
            : x.rootCompId) === pageId);
        return dashboardDescriptor?.routePath;
    }
    getDashboardRoutePath(devCenterComponentId) {
        const backOfficePage = this.appData?.components.find((x) => x.type === 'BACK_OFFICE_PAGE' && x.componentId === devCenterComponentId);
        if (!backOfficePage) {
            throw new Error(`Dashboard route path could not be found for componentId: ${devCenterComponentId}`);
        }
        return backOfficePage?.data?.routePath;
    }
}
//# sourceMappingURL=PanelsApi.js.map